import { type Time } from '@internationalized/date';
import React from 'react';
import { DateInput, DateSegment, Label, TimeField } from 'react-aria-components';
import NativeHourInput from './NativeHourInput';
import useHourField from './useHourField';

/**
 * HourField — the segmented hour picker (react-aria's `TimeField`) shared by `DatePickerShell` (popover pickers)
 * and `DatePickerInline`, so both render the same accessible, `--ui-*`-themed control instead of duplicating it.
 * The public contract stays on `@internationalized/date`'s `Time` (the pickers combine it with the date).
 *
 * On touch devices it renders `NativeHourInput` (a native `<input type="time">`) instead: react-aria's segments
 * summon the on-screen keyboard (which covers the picker popover, TAN-10405), while the native control opens
 * the OS time wheel.
 */

type HourFieldProps = {
  onChange: (time: Time | null) => void;
  value: Time | null;
};

function HourField({ onChange, value }: HourFieldProps): JSX.Element {
  const { isTouchDevice, t } = useHourField();

  if (isTouchDevice) {
    return <NativeHourInput onChange={onChange} value={value} />;
  }

  return (
    <TimeField aria-label={t('common:Hour')} className="date-picker-hour" hourCycle={24} onChange={onChange} value={value}>
      <Label className="date-picker-hour__label">{t('common:Hour')}</Label>
      <DateInput className="date-picker-hour__input">{(segment) => <DateSegment className="date-picker-hour__segment" segment={segment} />}</DateInput>
    </TimeField>
  );
}

export default HourField;
